import { addDays, eachDayOfInterval, max as latestOf, min as earliestOf, startOfToday } from 'date-fns';
import { toDateKey } from './dateKey';
import { FEED_CONTEXT_KEY } from './feedContext';
import { parseEventDate } from './eventMapper';
import { distanceKm, isUsableLocation } from './userLocation';
import {
  ANY_AUDIENCE_WIRE,
  getDefaultAudience,
  normalizeAudienceIds,
  normalizeFilterIds,
  restoreAudience,
  serializeAudience
} from './recommendationFilterOptions';

export const DEFAULT_RECOMMENDATION_FILTERS = {
  dateRange: 'all',
  customDateRange: null,
  venueSize: 'all',
  venueTypes: [],
  venueSetting: [],
  intents: [],
  // Adults (#717), frozen: every state spread from here shares this one array.
  audience: Object.freeze(getDefaultAudience()),
  sortBy: 'relevance',
  languages: [],
  freeOnly: false
};

export const DEFAULT_RECOMMENDATION_LIMIT = 100;

/**
 * The most the API will return in one request (`le=500` on `/recommendations`).
 *
 * The feed asks for `DEFAULT_RECOMMENDATION_LIMIT` — a ranked list nobody scrolls past
 * anyway. The map asks for this: it draws one whole day of a city and a cut one is a
 * filter nobody applied, on the one view whose job is coverage.
 */
export const MAX_RECOMMENDATION_LIMIT = 500;

/**
 * Order mapped events by how far their venue is from `userLocation`.
 *
 * Client-side like every other sort on the feed: the page already holds both points, and
 * the backend's own ordering blends distance with taste rather than exposing a pure one.
 * Events with no venue coordinates sort last in *both* directions — "farthest first"
 * means the farthest venue we know about, not the ones we cannot measure.
 */
export const compareEventsByDistance = (userLocation, direction = 'asc') => (a, b) => {
  const toPoint = (event) => ({ lat: event?.venueLatitude, lon: event?.venueLongitude });
  const da = distanceKm(userLocation, toPoint(a));
  const db = distanceKm(userLocation, toPoint(b));
  if (da === null || db === null) return (da === null ? 1 : 0) - (db === null ? 1 : 0);
  return direction === 'desc' ? db - da : da - db;
};

/**
 * Order a mapped feed for a `sortBy` value. Returns a new array.
 *
 * The one place the feed's order is decided, because two code paths land events on
 * screen — the filter/refetch path and the URL-driven fetch effect — and when only one
 * of them sorted, a navigation inside the same city silently returned the feed to the
 * backend's order while the sort pill still named the sort the reader had chosen.
 *
 * `relevance` sorts nothing on purpose: the response arrives ranked, so the backend's
 * order *is* the relevance order. (There is no per-event score to re-sort by — the API
 * sends none.)
 *
 * Distance needs a usable location; without one the events keep that same backend order,
 * which is where the dropdown falls back to as well once the address is cleared.
 */
export const sortFeedEvents = (events = [], sortBy, userLocation = null) => {
  const sorted = [...events];
  switch (sortBy) {
    case 'distance_asc':
    case 'distance_desc':
      if (isUsableLocation(userLocation)) {
        sorted.sort(compareEventsByDistance(userLocation, sortBy === 'distance_desc' ? 'desc' : 'asc'));
      }
      break;
    case 'date_asc':
      sorted.sort((a, b) => new Date(a.date) - new Date(b.date));
      break;
    case 'date_desc':
      sorted.sort((a, b) => new Date(b.date) - new Date(a.date));
      break;
    default:
      break;
  }
  return sorted;
};

/**
 * Fold a response's `availability` into the one already on screen.
 *
 * The API sends `languages: null` when it could not take the aggregation — unknown, as
 * opposed to `[]`, which means this pool genuinely has no languages. Keeping the last
 * known list in that case matches how a null `free_count` keeps the Free pill: a
 * transient Elasticsearch hiccup degrades the filter panel instead of removing it.
 *
 * Only within the same pool, though. `pool` identifies what was asked (city, and the
 * text query on search): carrying Paris's languages into a Berlin request that failed
 * would offer a filter that returns nothing there.
 *
 * @param {Object|null} previous - availability currently held by the page
 * @param {Object|undefined|null} incoming - `response.availability`
 * @param {string} pool - identity of what was queried, e.g. `${city}|${query}`
 * @returns {Object|null}
 */
export const mergeRecommendationAvailability = (previous, incoming, pool) => {
  const carried = previous?.pool === pool ? previous : null;

  // A response with no availability at all (an older API, a cached payload) is unknown
  // too — it is not evidence that this pool has nothing.
  if (!incoming) return carried;

  return {
    ...incoming,
    languages: incoming.languages ?? carried?.languages ?? [],
    pool
  };
};

/**
 * Read the two dynamic filter options back out of an `availability` block.
 *
 * Both halves treat *missing* as unknown rather than as none, which is the whole point of
 * the null the API sends when it could not take the aggregation: an empty language list
 * means this pool really has none, and only a `free_count` of exactly 0 hides the Free
 * pill. Written once because both surfaces read them, and a `> 0` in one of the two would
 * hide the filter on every failed read without anything looking wrong.
 */
export const availabilityLanguages = (availability) => availability?.languages ?? [];

export const availabilityHasFreeEvents = (availability) => availability?.free_count !== 0;

const normalizeVenueSettings = (value) => {
  if (Array.isArray(value)) {
    return [...new Set(value.map(v => String(v).toLowerCase()).filter(v => v === 'indoor' || v === 'outdoor'))];
  }

  const normalized = String(value || '').toLowerCase();
  if (!normalized || normalized === 'all') return [];
  if (normalized === 'mixed' || normalized === 'both') return ['indoor', 'outdoor'];
  if (normalized === 'indoor' || normalized === 'outdoor') return [normalized];
  return [];
};

const parseSavedDate = (value) => {
  if (value instanceof Date) return value;

  if (typeof value === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(value)) {
    const [year, month, day] = value.split('-').map(Number);
    return new Date(year, month - 1, day);
  }

  return new Date(value);
};

const parseSavedCustomDateRange = (customDateRange) => {
  if (!customDateRange?.from || !customDateRange?.to) return null;

  const from = parseSavedDate(customDateRange.from);
  const to = parseSavedDate(customDateRange.to);
  if (Number.isNaN(from.getTime()) || Number.isNaN(to.getTime())) return null;

  return { from, to };
};

/**
 * The filters as `user_preferences.filters` stores them, the inverse of
 * `restoreRecommendationFiltersFromPreferences`: plain values, dates as ISO strings, and the
 * audience as its URL token (null = the default, `any`, `for_children`) so an explicit Any
 * survives a cold entry. `sortBy` is a display preference and is left out.
 */
export const savedFilterPreferences = (filters = {}) => ({
  dateRange: filters.dateRange || 'all',
  customDateRange: filters.customDateRange
    ? {
        from: new Date(filters.customDateRange.from).toISOString(),
        to: new Date(filters.customDateRange.to).toISOString()
      }
    : null,
  venueSize: filters.venueSize || 'all',
  venueTypes: filters.venueTypes || [],
  venueSetting: filters.venueSetting || [],
  intents: filters.intents || [],
  audience: serializeAudience(filters.audience),
  languages: filters.languages || [],
  freeOnly: !!filters.freeOnly
});

export const restoreRecommendationFiltersFromPreferences = (preferences = {}, { urlFreeOnly = false } = {}) => {
  const defaults = {
    ...DEFAULT_RECOMMENDATION_FILTERS,
    freeOnly: urlFreeOnly
  };

  if (preferences?.filters) {
    const savedFilters = preferences.filters;
    return {
      ...defaults,
      dateRange: savedFilters.dateRange || defaults.dateRange,
      customDateRange: parseSavedCustomDateRange(savedFilters.customDateRange),
      venueSize: savedFilters.venueSize || defaults.venueSize,
      venueTypes: Array.isArray(savedFilters.venueTypes) ? savedFilters.venueTypes : [],
      venueSetting: normalizeVenueSettings(savedFilters.venueSetting),
      ...normalizeFilterIds({ intents: savedFilters.intents || preferences.intents }),
      audience: restoreAudience(savedFilters.audience ?? preferences.audience),
      languages: Array.isArray(savedFilters.languages) ? savedFilters.languages : [],
      freeOnly: urlFreeOnly || !!savedFilters.freeOnly
    };
  }

  return {
    ...defaults,
    ...normalizeFilterIds(preferences || {}),
    audience: restoreAudience(preferences?.audience),
    dateRange: preferences?.dates?.start_date ? 'custom' : 'all',
    customDateRange: preferences?.dates?.start_date
      ? parseSavedCustomDateRange({
          from: preferences.dates.start_date,
          to: preferences.dates.end_date
        })
      : null
  };
};

/**
 * Serialize active filters into URL query params (the canonical source of truth).
 *
 * Mirrors the API option shape (snake_case keys) so the URL reads like the
 * request it produces. `sortBy` is a client-side display preference and is
 * intentionally excluded. Empty/default values are omitted so a bare URL means
 * "the defaults" — no filters but the Adults audience — see
 * `restoreRecommendationFiltersFromUrl` for the inverse.
 */
export const buildUrlParamsFromFilters = (filters = {}, { defaultAudience } = {}) => {
  const params = new URLSearchParams();

  const { intents = [] } = filters;
  // The page's default audience is left out (search's is Any, the feed's Adults).
  const audience = serializeAudience(filters.audience, defaultAudience);
  if (intents.length > 0) params.set('intents', intents.join(','));
  if (audience) params.set('audience', audience);

  if (filters.venueSize && filters.venueSize !== 'all') {
    params.set('venue_size', filters.venueSize);
  }
  if (Array.isArray(filters.venueTypes) && filters.venueTypes.length > 0) {
    params.set('venue_types', filters.venueTypes.join(','));
  }

  const setting = Array.isArray(filters.venueSetting) ? filters.venueSetting : [];
  if (setting.length === 1) params.set('venue_modality', setting[0]);
  else if (setting.length === 2) params.set('venue_modality', 'both');

  if (Array.isArray(filters.languages) && filters.languages.length > 0) {
    params.set('languages', filters.languages.join(','));
  }
  if (filters.freeOnly) params.set('free_only', 'true');

  if (filters.dateRange && filters.dateRange !== 'all') {
    params.set('date', filters.dateRange);
    if (filters.dateRange === 'custom' && filters.customDateRange?.from && filters.customDateRange?.to) {
      params.set('date_from', toDateKey(new Date(filters.customDateRange.from)));
      params.set('date_to', toDateKey(new Date(filters.customDateRange.to)));
    }
  }

  return params;
};

/**
 * Restore filters from URL query params (string or URLSearchParams). The inverse
 * of `buildUrlParamsFromFilters`. Absent params resolve to defaults, so a bare
 * URL yields the default state: unfiltered, Adults audience.
 */
export const restoreRecommendationFiltersFromUrl = (searchParams, { defaultAudience } = {}) => {
  const params = searchParams instanceof URLSearchParams
    ? searchParams
    : new URLSearchParams(searchParams || '');

  const list = (key) => {
    const raw = params.get(key);
    return raw ? raw.split(',').map((s) => s.trim()).filter(Boolean) : [];
  };

  let dateRange = params.get('date') || 'all';
  let customDateRange = null;
  if (dateRange === 'custom') {
    customDateRange = parseSavedCustomDateRange({
      from: params.get('date_from'),
      to: params.get('date_to')
    });
    if (!customDateRange) dateRange = 'all';
  }

  return {
    ...DEFAULT_RECOMMENDATION_FILTERS,
    dateRange,
    customDateRange,
    venueSize: params.get('venue_size') || 'all',
    venueTypes: list('venue_types'),
    venueSetting: normalizeVenueSettings(params.get('venue_modality')),
    ...normalizeFilterIds({ intents: list('intents') }),
    audience: restoreAudience(list('audience'), defaultAudience),
    languages: list('languages'),
    freeOnly: params.get('free_only') === 'true'
  };
};

export const calculateDatesFromDateRange = (dateRange, customDateRange) => {
  if (dateRange === 'all') {
    return null;
  }

  if (dateRange === 'custom' && customDateRange?.from && customDateRange?.to) {
    return {
      start_date: toDateKey(new Date(customDateRange.from)),
      end_date: toDateKey(new Date(customDateRange.to))
    };
  }

  const today = new Date();
  today.setHours(0, 0, 0, 0);
  let startDate = new Date(today);
  let endDate = new Date(today);

  switch (dateRange) {
    case 'today':
      endDate.setHours(23, 59, 59, 999);
      break;
    case 'tomorrow':
      startDate.setDate(startDate.getDate() + 1);
      endDate.setDate(endDate.getDate() + 1);
      endDate.setHours(23, 59, 59, 999);
      break;
    case 'this_week':
      endDate.setDate(endDate.getDate() + 7);
      endDate.setHours(23, 59, 59, 999);
      break;
    case 'this_weekend': {
      const daysUntilSaturday = (6 - today.getDay() + 7) % 7;
      startDate.setDate(startDate.getDate() + daysUntilSaturday);
      endDate = new Date(startDate);
      endDate.setDate(endDate.getDate() + 1);
      endDate.setHours(23, 59, 59, 999);
      break;
    }
    case 'this_month':
      endDate = new Date(today.getFullYear(), today.getMonth() + 1, 0);
      endDate.setHours(23, 59, 59, 999);
      break;
    default:
      return null;
  }

  return {
    start_date: toDateKey(startDate),
    end_date: toDateKey(endDate)
  };
};

/**
 * How many days the map's date strip can hold.
 *
 * The strip is the map's whole answer to "show me another day" (#552), so a long custom
 * range must not render a year of chips nobody will scroll to. 60 is two months out, past
 * which a reader is picking a date range, not scrolling a strip. (An *unbounded* filter
 * gets no strip at all — see `mapViewDates`.)
 */
export const MAX_MAP_STRIP_DAYS = 60;

/**
 * The date filter the map applies for a reader who has chosen none: today and tomorrow.
 *
 * The map draws a day at a time, so it has to pick some days — and "what's on tonight,
 * and what's on tomorrow" is the question a map with no timeframe on it is being asked.
 * It is applied to the filter rather than kept inside the map, because a map narrower
 * than the timeframe pill above it is a filter nobody can see, let alone widen.
 *
 * A *custom* range rather than a preset of its own: the pill, the chip, the URL and
 * `calculateDatesFromDateRange` all already know that shape, and a new `dateRange` value
 * would have to be taught to each of them.
 */
/**
 * The date filter with no range on it — what "clear the dates" means, spelled once.
 *
 * Its opposite number is `mapDefaultDateRange()`, and the two are used together: the map
 * swaps one for the other on the way in and out, and the page swaps back before
 * remembering the filters. A field joining the date filter has to be named in both, which
 * is the whole reason they sit beside each other.
 */
export const NO_DATE_RANGE = { dateRange: 'all', customDateRange: null };

export const mapDefaultDateRange = () => {
  const today = startOfToday();
  return { dateRange: 'custom', customDateRange: { from: today, to: addDays(today, 1) } };
};

/**
 * Whether a filter is exactly the range above, still.
 *
 * This is what makes entering the map reversible: the range goes back to "any date" when
 * the reader leaves, unless they moved it themselves, and comparing the dates says which
 * happened without a flag to keep in step with the URL, a remount or a shared link.
 */
export const isMapDefaultDateRange = (filters = {}) => {
  const applied = calculateDatesFromDateRange(filters.dateRange, filters.customDateRange);
  if (!applied) return false;
  const expected = mapDefaultDateRange().customDateRange;
  return (
    applied.start_date === toDateKey(expected.from) &&
    applied.end_date === toDateKey(expected.to)
  );
};

/**
 * The filters as the map can actually draw them: never without dates.
 *
 * "No date filter at all" is a state the map has no way to show — it draws a day at a
 * time — so clearing the dates there lands on the range it opens with rather than on
 * nothing. Every control that clears a date goes through the page's one filter handler,
 * which is where this is applied, so the chip's X, the timeframe pill's, "Clear All" and
 * the picker all mean the same thing without each knowing about the map.
 *
 * A no-op off the map, and a no-op whenever the reader has a range of their own.
 */
export const ensureMapDateRange = (filters = {}, mapView = false) =>
  mapView && !calculateDatesFromDateRange(filters.dateRange, filters.customDateRange)
    ? { ...filters, ...mapDefaultDateRange() }
    : filters;

/**
 * The days the map offers, as `yyyy-MM-dd` — the reader's own date range, day by day.
 *
 * The map shows one day at a time, but it is *not* a date filter of its own: it stays
 * inside the range the reader already chose, so the strip and the timeframe pill can
 * never disagree. With no range chosen ("all", the default) that leaves **today and
 * tomorrow**: two months of chips is the map inventing a date filter the reader never
 * set, and "what's on tonight, and what's on tomorrow" is the question a map with no
 * timeframe on it is being asked. Anything past that is a range, and the timeframe pill
 * is where a reader asks for one.
 *
 * Yesterday is never offered, whatever the filter says: a custom range that started last
 * week is still a legitimate filter — the feed shows what is left of it — but a day that
 * has already happened is not a day to plan. That is the `latestOf` against today.
 *
 * Dates go through `parseEventDate`, never `new Date(str)`: a bare "yyyy-MM-dd" parsed
 * that way is UTC midnight, which is the previous calendar day west of it.
 */
export const mapViewDates = (filters = {}) => {
  const range = calculateDatesFromDateRange(filters.dateRange, filters.customDateRange);

  const today = startOfToday();
  // No range chosen: today and tomorrow, and no more. The map applies that same range to
  // the filter as it opens (`mapDefaultDateRange`), so this answers for the frame before
  // that lands — and for a reader who clears every filter while the map is open.
  if (!range) return [today, addDays(today, 1)].map(toDateKey);

  const start = latestOf([today, parseEventDate(range?.start_date) ?? today]);
  // The horizon is clamped before the interval is built, not after: a range of a year
  // would otherwise allocate 365 dates to throw 305 of them away.
  const horizon = addDays(start, MAX_MAP_STRIP_DAYS - 1);
  const end = earliestOf([horizon, parseEventDate(range?.end_date) ?? horizon]);

  // A range entirely in the past leaves nothing to show, and a strip with no chips is a
  // map with no day selected. Today is the honest answer: it is what the feed is showing.
  if (end < start) return [toDateKey(today)];

  return eachDayOfInterval({ start, end }).map(toDateKey);
};

/**
 * The day the strip is really on, given the days it offers.
 *
 * A chosen day is only ever a *request*: it arrives from a URL a reader may have shared
 * last week, or from a range they have since moved, and the strip can only sit on a day it
 * draws a chip for. Anything it does not offer falls back to the first day it does — today
 * in every case but a range that starts in the future.
 *
 * One definition, and the map is the reason it is a function rather than an effect:
 * `EventMap` *derives* the day it draws through this, so a range moving under it needs no
 * state to notice. `carryStripDay` below is the other reader — the page's own copy of the
 * choice goes through it wherever a filter moves, which is what keeps the two in step.
 *
 * Empty rather than null for "no day", the same value the page seeds and `EventMap`
 * defaults to: a second spelling of nothing only has to be normalised somewhere.
 */
export const mapStripDay = (day, dates = []) =>
  day && dates.includes(day) ? day : (dates[0] ?? '');

/**
 * The reader's *chosen* day, carried across a change to the filters — or dropped.
 *
 * The sibling of `mapStripDay`, and deliberately not the same answer. That one decides what
 * the map **draws**, so it always names a day: fall back to the first of the range and the
 * strip has a chip pressed. This one is a record of what the reader **asked for**, so it
 * falls back to nothing. A range that can no longer hold their day has not moved their
 * choice to a different day — it has ended it, and the map goes back to answering for
 * itself. Re-pinning here instead wrote `map_day=<today>` into the URL and the remembered
 * feed the first time anyone narrowed a timeframe, a day nobody picked, pinned in a link
 * they might share.
 *
 * Three rules, applied wherever the day or the range moves — the mount seed and both of the
 * page's `setFilters` handlers:
 *
 * - **Off the map there is no day.** It is the strip's cursor, and the strip is the map's;
 *   a day kept while the list is on screen is state nothing can see, and it would make
 *   toggling back to the map disagree with reloading the very same URL.
 * - **A day nobody picked stays unpicked**, so a bare map link stays bare rather than
 *   acquiring today's date the first time any other filter moves.
 * - **A day the new range cannot draw is dropped**, not parked. The map is already drawing
 *   the first day of the new range; a choice left behind in state would jump back the
 *   moment the range widened again, onto a day the reader was never shown.
 */
export const carryStripDay = (day, filters = {}, mapView = false) =>
  mapView && day && mapViewDates(filters).includes(day) ? day : '';

export const buildRecommendationOptionsFromFilters = (filters = {}) => {
  const options = {};

  const dates = calculateDatesFromDateRange(filters.dateRange, filters.customDateRange);
  if (dates) {
    options.start_date = dates.start_date;
    options.end_date = dates.end_date;
  }

  const { intents = [] } = filters;
  const audience = normalizeAudienceIds(filters.audience);

  if (intents.length > 0) {
    options.intents = intents;
  }

  // Always sent: Any as every value (ANY_AUDIENCE_WIRE), never as nothing.
  options.audience = audience.length > 0 ? audience : [...ANY_AUDIENCE_WIRE];

  if (filters.venueSize && filters.venueSize !== 'all') {
    options.venue_size = filters.venueSize;
  }

  if (filters.venueTypes && filters.venueTypes.length > 0) {
    options.venue_types = filters.venueTypes;
  }

  if (Array.isArray(filters.venueSetting) && filters.venueSetting.length === 1) {
    options.venue_modality = filters.venueSetting[0];
  } else if (Array.isArray(filters.venueSetting) && filters.venueSetting.length === 2) {
    options.venue_modality = 'both';
  }

  if (Array.isArray(filters.languages) && filters.languages.length > 0) {
    options.languages = filters.languages;
  }

  if (filters.freeOnly) {
    options.free_only = true;
  }

  return options;
};

export const withRecommendationFetchLimit = (options = {}) => ({
  ...options,
  limit: DEFAULT_RECOMMENDATION_LIMIT
});

/**
 * Build everything a cold app-entry (HomeRedirect / OAuth / email login /
 * onboarding submit) needs to land the user on the recommendations page with
 * the list, the URL, and the visible filter chips all in agreement.
 *
 * Derives the page filter-state from saved preferences once, then returns both
 * the API `options` (what the backend filters by) and the URL `params` (what the
 * page seeds its filter UI from) from that single source. Returning users see
 * their last-used filters re-applied AND reflected.
 */
export const buildRecommendationEntryFromPreferences = (preferences = {}) => {
  const pageFilters = restoreRecommendationFiltersFromPreferences(preferences);
  return {
    pageFilters,
    options: buildRecommendationOptionsFromFilters(pageFilters),
    params: buildUrlParamsFromFilters(pageFilters)
  };
};

/**
 * The map view, as the feed URL carries it.
 *
 * It is not a filter — it switches how the feed is read, nothing about what is
 * fetched — but it lives in the URL anyway, because it has to survive leaving the
 * page: a reader who opens an event from the map belongs back on the map.
 *
 * The param is still `now=1` rather than something that says "map". It was written
 * when the map was a "right now" mode that also forced today (#552 took the date
 * override away), and the links are already out there — in shared URLs, in
 * `recs_last_search`, in every breadcrumb this app has handed out. Renaming it
 * would drop those readers on a list. The name is the only thing left of the mode.
 */
export const isMapViewUrl = (search) =>
  new URLSearchParams(search || '').get('now') === '1';

/**
 * The event-type pill, as the feed URL carries it.
 *
 * Like the mode, it is not a filter: it cuts the list the API already returned and never
 * reaches a request, so it lives nowhere near `filters`. It lives in the URL for the same
 * reason the mode does — a reader who narrowed the feed to Comedy, opened an event and
 * came back was handed the whole feed again.
 *
 * Read raw, not through `normalizePrimaryEventType`: `other` is a real pill id that the
 * normalizer maps to '' by design, so validating here would silently drop that one pill.
 * What rejects a type that is not on offer is the page's own effect comparing it against
 * the options the current results actually produced.
 */
export const primaryEventTypeFromUrl = (search) =>
  new URLSearchParams(search || '').get('event_type') || '';

/**
 * The map's selected day, as the feed URL carries it (`map_day=2026-03-15`).
 *
 * The third thing that is not a filter but has to survive leaving the page — and the one
 * this page kept losing. The view came back on `now=1` and the pill on `event_type`, and
 * the reader still landed on today, because the day the strip was on lived only in the
 * map's own state and every way back remounts it (#578).
 *
 * `map_day` rather than `day`: `date`, `date_from` and `date_to` are already the date
 * *filter* in this query string, and this is a cursor inside what they allow.
 *
 * Read raw, like the pill. What rejects a stale or invented value is `mapStripDay`,
 * against the days the strip is actually offering — the only place that knows.
 */
export const mapDayFromUrl = (search) =>
  new URLSearchParams(search || '').get('map_day') || '';

/**
 * The feed's full query string: the filters, plus the four things that are not filters
 * and so would be dropped by a filter-driven rewrite — the city, the map view, the
 * event-type pill and the map's day. `buildUrlParamsFromFilters` only knows about
 * filters, which is why city used to be re-set by hand at every call site.
 */
export const buildFeedUrlParams = (filters, { city, mapView, eventType, mapDay } = {}) => {
  const params = buildUrlParamsFromFilters(filters);
  if (city) params.set('city', city);
  if (mapView) params.set('now', '1');
  if (eventType) params.set('event_type', eventType);
  // Serialized, never clamped. This used to re-derive `mapViewDates(filters)` and clamp the
  // day on the way out, which read as safe and was the opposite: it recomputes the range
  // from the wall clock, while the strip on screen draws from a memo keyed on the filters.
  // A tab left open across midnight then wrote a day the reader was not looking at — the
  // one disagreement between the URL and the chip that the clamp existed to prevent.
  // `carryStripDay` owns that rule now, applied where the day is owned; this writes it down.
  if (mapView && mapDay) params.set('map_day', mapDay);
  return params;
};


// A feed URL always carries a city (the page normalises one in on mount), so a query
// string without one is not a feed to go back to — a detail page's own `?id=`, say.
const asFeedSearch = (search) =>
  new URLSearchParams(search || '').get('city') ? search : '';

/**
 * Remember the feed the reader last had open, as its own query string: city,
 * filters and the map-view flag together.
 *
 * The recommendations page is the only writer. Every path back to the feed reads it
 * as the stand-in for an origin URL it was never given — the map panel's rows carry
 * none at all, and a related-event card on a detail page carries that page's query
 * string, which is not a feed. Without it those land on a bare city link: the feed
 * reset to its defaults, which is what reads as "why am I back on the city I
 * onboarded with".
 *
 * sessionStorage, not localStorage: this is "where I was a moment ago", per tab. A
 * fresh tab — a shared link — still opens clean, which is what the bare-URL contract
 * promises.
 */
export const rememberFeedContext = (search) => {
  try {
    sessionStorage.setItem(FEED_CONTEXT_KEY, search || '');
  } catch {
    // Private mode / storage disabled: the back-link falls back to the event's city.
  }
};

// Module-private on purpose: `buildFeedBackLink` and `buildRecommendationEntry` below are
// the only two readers, and they are the ones that know what a remembered feed means. A
// third reader elsewhere would be a second opinion about that.
const recallFeedContext = () => {
  try {
    return sessionStorage.getItem(FEED_CONTEXT_KEY) || '';
  } catch {
    return '';
  }
};

/**
 * Where a page that is not the feed sends the reader back to it, and what that crumb
 * is called.
 *
 * The remembered feed context is read *before* `fromUrl`, even though `fromUrl` is the
 * more specific-looking of the two. `fromUrl` is `window.location.search` as the card
 * was clicked, and the address bar is not always what the feed was showing: the feed
 * syncs itself with `history.replaceState`, so browser Back can restore an older bar
 * over a page that never re-rendered for it — a bar still saying `now=1` above a list
 * the reader had just left the mode on. The remembered value is built from that page's
 * own state and so cannot disagree with it. `fromUrl` stays as the fallback for when
 * there is nothing remembered (storage disabled, or a tab that has not opened the feed).
 *
 * The label comes out of whichever query string wins, so the crumb cannot name one city
 * while linking to another.
 */
export const buildFeedBackLink = ({ fromUrl = '', eventCity = '', fallbackCity = '' } = {}) => {
  const search = asFeedSearch(recallFeedContext()) || asFeedSearch(fromUrl);
  const city = new URLSearchParams(search || '').get('city') || eventCity || fallbackCity || '';
  const query = search || (city ? `?city=${encodeURIComponent(city)}` : '');
  return { href: `/event-recommendations${query}`, city };
};

/**
 * Where "home" should land: the feed the reader was last looking at — city, filters
 * and mode — falling back to `buildRecommendationEntryFromPreferences` on a genuinely
 * cold entry (a new tab, or a session that has not opened the feed yet).
 *
 * Saved preferences are not "where I was". `preferences.city` is written once at
 * onboarding and never updated, and the saved filters are the *last set applied*, not
 * the set currently on screen — so rebuilding from them is how clicking the app logo
 * drops a reader who switched city an hour ago back on the city they onboarded with,
 * and a reader who was on the map back on a list.
 *
 * `city` is null when there is no remembered feed, which is the caller's cue to fall
 * back to `getPreferredCity`.
 */
export const buildRecommendationEntry = (preferences = {}) => {
  const remembered = asFeedSearch(recallFeedContext());
  if (!remembered) return { ...buildRecommendationEntryFromPreferences(preferences), city: null };

  const params = new URLSearchParams(remembered);
  const pageFilters = restoreRecommendationFiltersFromUrl(params);
  return {
    pageFilters,
    // The view the reader was in changes nothing about the request: the map reads the
    // same feed the list does, for the same dates.
    options: buildRecommendationOptionsFromFilters(pageFilters),
    params,
    city: params.get('city')
  };
};

/**
 * Turn the nav data a cold entry prefetched (`{ city, recommendations, filters,
 * search }`) into the recommendations-page path to navigate to. `search` is what
 * buildRecommendationEntryFromPreferences produced; fall back to a bare city
 * query for the error / onboarding-fallback paths that lack it.
 */
export const recommendationsPathFromNavData = (navData = {}) => {
  const search = navData.search
    || (navData.city ? `city=${encodeURIComponent(navData.city)}` : '');
  return search ? `/event-recommendations?${search}` : '/event-recommendations';
};
